import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { FederatedInfoResult } from '@shared/federatedinforesult';
import { ExternalUrlService } from '@shared/services/externalurl.service';
import { IReport, ReportService } from '@shared/services/report.service';
import { catchError, combineLatest, delay, map, Subject, take, takeUntil, throwError } from 'rxjs';
import { MyErrorStateMatcher } from '../../../auth/sign-in/sign-in.component';
import { IInsightsUser, InsightsService } from '../../../shared/services/insights.service';
import { SessionStorageService } from '../../../shared/services/storage.service';
import { IUserAccess, UserService } from '../../../shared/services/user.service';
import { IInviteUser, IUserPermission, IUserPolicies, IUserPolicy, UserPermissionsService } from '../../../shared/services/userpermissions.service';
import { DialogUserReportsComponent } from './reports/dialog-user-reports/dialog-user-reports.component';

@Component({
	selector: 'app-users',
	templateUrl: './users.component.html',
	styleUrls: ['./users.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class UsersComponent implements OnInit, AfterViewInit, OnDestroy {
	displayedColumns: string[] = ['select', 'name', 'email', 'grantType', 'adminuser', 'reportmanager', 'interval', 'menu'];

	dataSource = new MatTableDataSource();

	showSearch = false;
	resultsLength = 0;
	selected: string[] = [];
	isAllSelected: boolean = false;
	isLoading = true;
	unsubscribe: Subject<void> = new Subject<void>();

	domains: string[] = ['acies.dk', 'inwido.com', 'svane.com', 'kreum.dk', 'unikfunkis.dk', 'jabsgroup.com', 'kastrupvinduet.dk', 'hanstholm.com', 'boejsoe.dk', 'idealcombi.dk', 'hvidbjergvinduet.dk', 'vindunor.dk'];

	searchText = '';

	private destroyNotifier$ = new Subject<void>();
	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'insights.administration.users.newbtn',
			click: () => this.openUserAccess(null),
		},
	];

	constructor(
		private userPermissionsService: UserPermissionsService,
		public dialog: MatDialog,
		private insightsService: InsightsService,
		private sessionStorageService: SessionStorageService,
		private userService: UserService,
		private reportService: ReportService,
		private externalUrlService: ExternalUrlService,
		private translator: TranslateService,
		public notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		this.userService.account$.pipe(takeUntil(this.destroyNotifier$)).subscribe((acc) => {
			if (acc) {
				this.userPermissionsService.fetchUserPermissions(acc);
			}
		});
	}

	ngOnDestroy(): void {
		this.destroyNotifier$.next();
		this.destroyNotifier$.complete();
	}

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	ngAfterViewInit() {
		this.userPermissionsService.userPermissions$
			.pipe(
				delay(0),
				takeUntil(this.destroyNotifier$),
				map((r) => {
					const userAccess = r.reduce((a, v) => {
						a.push({
							id: v.id,
							name: v.name,
							email: v.email,
							interval: 0,
							permissionCodes: v.permissionCodes,
							reportmanager: !!((v.permissionCodes.includes('insights:ReportCreate') && !v.permissionCodes.includes('insights:UserCreate')) || v.permissionCodes.includes('insights:UserCreate')),
							adminuser: !!v.permissionCodes.includes('insights:UserCreate'),
							grantType: 'account',
						});
						return a;
					}, [] as IUserAccess[]);
					this.resultsLength = userAccess.length;
					return userAccess;
				}),
			)
			.subscribe((p) => {
				this.insightsService
					.fetchUsers()
					.pipe(takeUntil(this.destroyNotifier$))
					.subscribe({
						next: (data) => {
							this.updateTableData(data.result, p);
							this.isLoading = false;
						},
						error: () => (this.isLoading = false),
						complete: () => {},
					});
			});

		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;

		// Don't sort lowercase after uppercase
		this.dataSource.sortingDataAccessor = (item, property) => {
			if (typeof item[property] === 'boolean' || typeof item[property] === 'number') {
				return item[property];
			}
			return !item[property] ? '' : item[property].toLocaleLowerCase();
		};

		this.sort.sortChange.pipe(takeUntil(this.destroyNotifier$)).subscribe(() => {
			this.paginator.firstPage();
		});
	}

	handleError(error: { error: { message: string }; status: any; message: any }) {
		let errorMessage = '';
		if (error.error instanceof ErrorEvent) {
			// Get client-side error
			errorMessage = error.error.message;
		} else {
			// Get server-side error
			errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}
		console.log(errorMessage);
		return throwError(() => new Error(errorMessage));
	}

	updateTableData(users: IInsightsUser[], userAccs: IUserAccess[]) {
		// this.userPermissionService.userPermissions.filter((item: IUserPermission) => item.email == this.email)
		// var userAccess: IUserAccess[] = this.dataSource.data as IUserAccess[];
		const updatedUserAccess: IUserAccess[] = [];
		userAccs.forEach((element) => {
			const emailsplit = element.email.split('@', 2);
			const domain = emailsplit[1];
			const prefix = emailsplit[0];
			const plus = prefix.split('+');
			const filteredUsers: IInsightsUser[] = users.filter((item: IInsightsUser) => item.id === element.id);
			if (filteredUsers && filteredUsers.length > 0) {
				element.interval = filteredUsers[0].slideshowInterval ? filteredUsers[0].slideshowInterval : 0;
				element.defaultViewId = filteredUsers[0].defaultViewId;
				element.defaultViewType = filteredUsers[0].defaultViewType;
			}
			element.grantType = this.domains.includes(domain.toLowerCase()) && (plus.length !== 2 || (plus.length === 2 && plus[1] === '')) ? 'managed' : 'account';
			updatedUserAccess.push(element);
		});
		this.dataSource.data = updatedUserAccess;
	}

	applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.searchText = filterValue;
		this.dataSource.filter = filterValue.trim().toLowerCase();
	}

	openUserAccess(userAccess: IUserAccess | null) {
		const dialogRef = this.dialog.open(DialogUserAccessDialogComponent, {
			width: '450px',
			data: userAccess,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroyNotifier$))
			.subscribe(() => {
				this.userPermissionsService.fetchUserPermissions(this.userService.account);
			});
	}

	editAccess(userAccess: IUserAccess) {
		console.log(`open edit access dialog: ${JSON.stringify(userAccess)}`);
		this.openUserAccess(userAccess);
	}

	editUserReports(userAccess: IUserAccess) {
		const dialogRef = this.dialog.open(DialogUserReportsComponent, {
			width: '700px',
			data: userAccess,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroyNotifier$))
			.subscribe(() => {
				this.insightsService.fetchUsers().subscribe((data) => {
					this.updateTableData(data.result, this.dataSource.data as IUserAccess[]);
				});
				combineLatest([
					this.reportService.fetchUserReportsById(this.userService.impersonateUser ? this.userService.impersonateUser.id : this.sessionStorageService.get('User.IAM.Id')),
					this.externalUrlService.fetchUserExternalUrlsById(this.userService.impersonateUser ? this.userService.impersonateUser.id : this.sessionStorageService.get('User.IAM.Id')),
				]).subscribe((data) => {
					if (data[0] && data[1]) {
						const extUrlsReports: IReport[] = data[1].result;
						extUrlsReports.forEach((r) => {
							r.externalId = r.url;
						});
						this.reportService.userReports = [...data[0].result, ...extUrlsReports];
					}
				});
			});
	}

	loginAsUser(userAccess: IUserAccess) {
		this.userService.impersonateUser = userAccess;
		this.notificationService.notify(
			['insights.administration.users.loginas_success_header'],
			[
				'insights.administration.users.loginas_success_content',
				{
					name: userAccess.name ? userAccess.name : '[NO NAME]',
					email: userAccess.email,
				},
			],
			'success',
		);
	}

	enableLoginAs(userAccess: IUserAccess) {
		return userAccess.email !== this.sessionStorageService.get('User.Email');
	}

	select(userAccess: IUserAccess) {
		console.log('selected');
		const index = this.selected.indexOf(userAccess.id, 0);
		if (index > -1) {
			this.selected.splice(index, 1);
		} else {
			this.selected.push(userAccess.id);
		}
		this.isAllSelected = this.dataSource.data.length === this.selected.length;
	}

	canDeleteUser(userAccess: IUserAccess): boolean {
		if (userAccess.email.includes('+')) {
			return true;
		} else {
			let domain = userAccess.email;
			domain = domain.split('@', 2)[1];
			if (this.domains.findIndex((x) => x.toLowerCase() === domain.toLowerCase()) > -1) {
				return false;
			}
			return true;
		}
	}

	onDeleteUser(userAccess: IUserAccess) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true, // Data to be returned on OK click
				cancelReturnData: false, // Data to be returned on CANCEL click
				heading: this.translator.instant('insights.administration.users.deleteDialogHeading'),
				content: this.translator.instant('insights.administration.users.deleteDialogContent', { name: userAccess.name, email: userAccess.email }),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result === true) {
					this.selected = [];
					this.isAllSelected = false;
					this.insightsService
						.deleteUser(userAccess.id)
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.deleteIAMUser(userAccess);
							},
							error: (err: Error) => {
								if (err.message.includes('404')) {
									this.deleteIAMUser(userAccess);
								}
							},
						});
				}
			});
	}

	deleteIAMUser(userAccess: IUserAccess) {
		this.userService
			.deleteIAMUser(userAccess.id)
			.pipe(take(1))
			.subscribe(() => {
				console.log(`Deleted iam user: ${userAccess.id}`);
				this.userPermissionsService.clearUserPermissionsCache(this.userService.account);
				this.userPermissionsService.fetchUserPermissions(this.userService.account);
				this.notificationService.notify(
					['insights.administration.users.userDeletedNotificationHeader'],
					[
						'insights.administration.users.userDeletedNotificationContent',
						{
							name: userAccess.name ? userAccess.name : '[NO NAME]',
							email: userAccess.email,
						},
					],
					'success',
				);
			});
	}

	selectSingle(userAccess: IUserAccess) {
		this.selected = [];
		this.isAllSelected = false;
		this.selected.push(userAccess.id);
	}

	selectOrClearAll() {
		if (this.dataSource.data.length === this.selected.length) {
			this.selected = [];
			this.isAllSelected = false;
		} else {
			this.selected = [];
			this.dataSource.data.forEach((element: IUserAccess) => {
				this.selected.push(element.id);
			});
			this.isAllSelected = true;
		}
	}

	sendInviteToken() {
		const inviteUsers: IUserAccess[] = [];
		const notAllowedUsers: IUserAccess[] = [];
		const loggedInUser = this.sessionStorageService.get('User.Email');
		let domain = '';
		domain = loggedInUser;
		domain = domain.split('@', 2)[1];

		this.dataSource.data.forEach((element: IUserAccess) => {
			const index = this.selected.findIndex((i) => i === element.id);
			const emailSplit = element.email.split('@', 2);
			const userDomain = emailSplit[1];
			const plus = emailSplit[0].split('+');
			if (index > -1) {
				if ((domain === 'acies.dk' || userDomain === domain) && (plus.length !== 2 || (plus.length === 2 && plus[1] === ''))) {
					inviteUsers.push(element);
				} else {
					notAllowedUsers.push(element);
				}
			}
		});

		inviteUsers.forEach((element: IUserAccess) => {
			this.userService.sendInviteTokenByAccount(element.id);
			this.notificationService.notify(['insights.administration.users.successinviteheader'], ['insights.administration.users.successinvitecontent', { email: element.email }], 'success');
		});

		notAllowedUsers.forEach((element: IUserAccess) => {
			this.notificationService.notify(['insights.administration.users.failureinviteheader'], ['insights.administration.users.failureinvitecontent', { email: element.email }], 'error');
		});

		this.isAllSelected = false;
		this.selected = [];
	}
}

@Component({
	selector: 'app-dialog-user-access',
	templateUrl: 'dialog-user-access.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class DialogUserAccessDialogComponent implements OnDestroy {
	stepperText = '<strong><u>1</u></strong> - 2 - 3';
	stepperText1 = '<strong><u>1</u></strong> - 2 - 3';
	stepperText2 = '1 - <strong><u>2</u></strong> - 3';
	stepperText3 = '1 - 2 - <strong><u>3</u></strong>';
	stepperText1b = '<strong><u>1</u></strong>';
	// stepperText1b = '<strong><u>1</u></strong> - 2';
	// stepperText2b = '1 - <strong><u>2</u></strong>';
	stepper = 1;

	form: FormGroup;
	email: string = '';
	policyControl = new FormControl<any>('empty');
	slideshowControl = new FormControl<number>(0);
	emailControl = new FormControl<string>('', [Validators.email]);
	nameControl = new FormControl<string>('');
	matcher = new MyErrorStateMatcher();

	nameDisabled: boolean = false;
	userExist: boolean = false;
	userMustExist: boolean = false;
	existingUserId: string = '';
	editUser = false;
	showError = false;
	showRoleError = false;
	domainFederated = false;
	nameError = false;
	nextIsDisabled = false;

	private destroyNotifier$ = new Subject<void>();

	policies: IUserPolicy[];
	// = [
	//   { name: 'Vis rapporter', id: '123' },
	//   { name: 'Rapport manager', id: '234' },
	//   { name: 'Bruger administrator', id: '345' },
	// ];

	permissions =
		'insights:test,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2,\r\ninsights:test2';

	constructor(
		public dialogRef: MatDialogRef<DialogUserAccessDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: IUserAccess,
		public userService: UserService,
		public userPermissionService: UserPermissionsService,
		public insightsService: InsightsService,
		public dialog: MatDialog,
		private translator: TranslateService,
		private notificationService: NotificationService,
	) {
		this.stepper = data == null ? 1 : 2;
		this.stepperText = data == null ? this.stepperText1 : this.stepperText1b;
		this.form = new FormGroup<any>({
			email: this.emailControl,
			name: this.nameControl,
			policy: this.policyControl,
			interval: this.slideshowControl,
		});
		if (data != null) {
			this.existingUserId = data.id;
			this.editUser = true;
			this.emailControl.setValue(data.email);
			this.nameControl.setValue(data.name);
			this.slideshowControl.setValue(data.interval);
			this.searchPolicies();
		}
	}

	ngOnDestroy(): void {
		this.destroyNotifier$.next();
		this.destroyNotifier$.complete();
	}

	onNoClick(): void {
		this.dialogRef.close();
	}

	next(): void {
		this.nextIsDisabled = true;
		this.userExist = false;
		this.userMustExist = false;
		if (this.stepper === 1) {
			this.stepperText = this.stepperText2;
			this.stepper++;
		} else if (this.stepper === 2) {
			this.stepperText = this.data == null ? this.stepperText3 : this.stepperText1b;
			this.stepper++;
		}
	}

	previous(): void {
		this.userExist = false;
		this.userMustExist = false;
		if (this.stepper === 3) {
			this.stepperText = this.data == null ? this.stepperText2 : this.stepperText1b;
			this.stepper--;
		} else if (this.stepper === 2) {
			this.stepperText = this.stepperText1;
			this.stepper--;
		}
	}

	searchUser() {
		this.email = this.emailControl.value.trim();
		if (this.email !== '') {
			const userPermission: IUserPermission[] = this.userPermissionService.userPermissions.filter((item: IUserPermission) => item.email === this.email);
			this.handleUserData(userPermission);

			if (
				!this.email.includes('+') ||
				this.email.split('@')[0].split('+').length !== 2 ||
				this.email
					.split('@')[0]
					.split('+')
					.filter((part) => part.trim()).length === 1
			) {
				this.userService
					.getFederatedInfo(this.email)
					.pipe(
						take(1),
						catchError(() => this.handleUserNotFederated()),
					)
					.subscribe((data) => this.handleFederatedInfo(data));
			} else {
				this.domainFederated = false;
				const localpart = this.email.split('+')[0];
				const domain = this.email.split('@')[1];
				const email = `${localpart}@${domain}`;
				const userPermission: IUserPermission[] = this.userPermissionService.userPermissions.filter((item: IUserPermission) => item.email === email);
				this.handleUserMustExistForPlusAdress(userPermission);
			}
		}
	}

	handleFederatedInfo(_: FederatedInfoResult) {
		this.domainFederated = true;
		this.userExist = false;
		this.userMustExist = false;
	}

	handleUserNotFederated(): [] {
		const userPermission: IUserPermission[] = this.userPermissionService.userPermissions.filter((item: IUserPermission) => item.email === this.email);
		if (userPermission && userPermission.length > 0) {
			this.userExist = true;
		} else {
			this.searchPolicies();
			this.next();
		}
		this.domainFederated = false;
		return [];
	}

	handleUserMustExistForPlusAdress(data: any): [] {
		if (data?.length === 0) {
			this.userMustExist = true;
		} else {
			this.userMustExist = false;
			this.handleUserNotFederated();
		}
		return [];
	}

	handleUserData(userData: IUserPermission[]) {
		if (userData?.length > 0) {
			this.emailControl.setValue(userData[0].email);
			this.nameControl.setValue(userData[0].name);
			this.nameDisabled = true;
			this.existingUserId = userData[0].id;
		} else {
			this.emailControl.setValue(this.email);
			this.nameControl.setValue('');
			this.policyControl.setValue('empty');
			this.nameDisabled = false;
		}
	}

	searchPolicies() {
		this.userPermissionService.fetchUserPolices(this.userService.account).subscribe((data: IUserPolicies) => {
			this.loadPolicies(data.result.filter((item: IUserPolicy) => item.name.startsWith('InsightsReportView')));
		});
	}

	loadPolicies(policies: IUserPolicy[]) {
		this.policies = policies;
	}

	onFocusOutEvent(_: any) {
		this.showError = !this.slideshowControl.valid;
	}

	onKeyDown(event) {
		if (event.keyCode === 13) {
			this.saveUser();
		}
	}

	validateUser(): boolean {
		if (!this.editUser) {
			const name = this.nameControl.value.trim();
			if (!name || name.length <= 0) {
				this.nameError = true;
				return false;
			}
			this.nameError = false;
		}
		if (!this.slideshowControl.valid) {
			this.showError = true;
			return false;
		} else {
			if (!this.editUser) {
				if (this.policyControl.value === 'empty') {
					this.showRoleError = true;
					return false;
				} else {
					this.permissions = (this.policyControl.value as IUserPermission).permissionCodes.join(',\r\n');
					this.next();
					return true;
				}
			}
			return true;
		}
	}

	saveUser() {
		if (this.validateUser()) {
			const userEmail = this.form.controls['email'].value;
			const userName = this.form.controls['name'].value;
			const user: IInsightsUser = {
				externalId: '',
				slideshowInterval: 0,
			};
			user.slideshowInterval = this.form.controls['interval'].value;

			if (this.existingUserId == null || this.existingUserId === '') {
				const inviteUser: IInviteUser = {
					email: userEmail,
					name: userName,
					policyId: (this.form.controls['policy'].value as IUserPermission).id,
				};
				this.userPermissionService.inviteUserWithPermissions(inviteUser).subscribe((data) => {
					user.externalId = data.result.userId;
					this.insightsService.addUser(user).subscribe(() => {
						this.dialogRef.close();
						this.notificationService.notify(['insights.administration.users.useraccess.successheader'], ['insights.administration.users.useraccess.successcontent', { name: userName, email: userEmail }], 'success');
					});
				});
			} else {
				user.externalId = this.existingUserId;
				this.insightsService.addUser(user).subscribe(() => {
					this.dialogRef.close();
					this.notificationService.notify(['insights.administration.users.useraccess.successheaderUpdated'], ['insights.administration.users.useraccess.successcontentUpdated', { name: userName, email: userEmail }], 'success');
				});
			}
		}
	}

	onPolicyChange(policy) {
		if (policy) {
			this.showRoleError = false;
		}
	}

	alphaNumberOnly(e) {
		// Accept only alpha numerics, not special characters
		const regex = /^[a-zæøå0-9 ]+$/i;
		const str = String.fromCharCode(!e.charCode ? e.which : e.charCode);
		if (regex.test(str)) {
			return true;
		}

		e.preventDefault();
		return false;
	}

	onPaste(e) {
		e.preventDefault();
		return false;
	}
}
